/**
 * CrawlLink
 *
 * A real <a href> around a card's name or picture, so search engines can find
 * the page behind it, while a tap still does exactly what the card always did.
 *
 * Why it exists (14 Oct 2026): every shop and product card opened its page from
 * a click handler (`history.push`). Google only follows real links, so it
 * could reach the menu pages and nothing else: the homepage showed 100 images
 * and linked to no shop, a storefront showed 50 products and linked to none.
 *
 *   - Plain click: the browser's navigation is cancelled and `onNavigate` runs
 *     (pass the card's own handler, so the redux state and route state it sets
 *     still happen). Without `onNavigate`, it pushes `to`.
 *   - Ctrl / Cmd / Shift / middle click: left to the browser, so "open in new
 *     tab" works for the first time.
 *   - The click never bubbles to the card around it, so the card's own
 *     onClick cannot navigate a second time.
 *
 * Do not wrap a whole card that contains buttons (add to cart, share): a
 * button inside a link is invalid HTML and the two clicks fight. Wrap the
 * picture and the name instead.
 */
import React from "react";
import { useHistory } from "react-router-dom";

const hrefOf = (to) =>
  typeof to === "string" ? to : `${(to && to.pathname) || ""}${(to && to.search) || ""}${(to && to.hash) || ""}`;

const opensElsewhere = (event) =>
  event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

export default function CrawlLink({ to, onNavigate, children, style, ...rest }) {
  const history = useHistory();

  const handleClick = (event) => {
    event.stopPropagation();
    if (event.defaultPrevented || opensElsewhere(event)) return;
    event.preventDefault();
    if (onNavigate) onNavigate(event);
    else history.push(to);
  };

  return (
    <a
      href={hrefOf(to)}
      onClick={handleClick}
      style={{ color: "inherit", textDecoration: "none", ...style }}
      {...rest}
    >
      {children}
    </a>
  );
}
